import {ReactNode, useCallback, useRef, useState} from 'react';
import {Fade} from '@esgi/ui-kit/transition';
import {composeHOC, join} from '@esgi/ui-kit/utils';
import {Layer, Overlay, withErrorHandling, useKeyboardEffect, ElementsNavigator, NavigatorManager} from '@esgi/ui-kit/core';
import {ModalContext} from './modal-context';
import {Selectors} from './modal.selectors';
import {ModalManagerProps, withModalManager} from './with-modal-manager';

import styles from './modal.module.less';

export type ModalProps = {
	unsafeDesiredZIndex?: number;
	className?: string;
	containerClassName?: string;
	children?: ReactNode;
	owner?: HTMLElement;
	'data-cy'?: string;
};

function Modal({open, onTransitionStateChanged, children, owner, className, containerClassName, unsafeDesiredZIndex, ...props}: ModalProps & ModalManagerProps): JSX.Element {
	const [labelID, setLabelID] = useState<string>('');
	const [contentID, setContentID] = useState<string>('');
	const isTop = useRef<boolean>(false);


	const navigator = useRef<NavigatorManager>();

	const handler = useCallback((event: KeyboardEvent) => {
		if (isTop.current) {
			return;
		}

		if (event.key === 'Tab') {
			let moved: boolean;
			if (event.shiftKey) {
				moved = navigator.current?.back();
			} else {
				moved = navigator.current?.forward();
			}

			if (moved) {
				event.preventDefault();
				event.stopPropagation();
			}
		}
	}, [isTop]);

	useKeyboardEffect('keydown', handler);

	return <ModalContext.Provider value={{setLabelMeta: setLabelID, setContentMeta: setContentID}}>
		<Fade in={open}
		      animateOnMount
		      onStateChanged={onTransitionStateChanged}
		      duration='fast'>
			<ElementsNavigator navigatorManager={navigator}>
				<Layer className={join(styles.modalLayer, containerClassName)}
				       inactive={!open}
				       owner={owner}
				       isTop={isTop}
				       zIndex={unsafeDesiredZIndex}>
					<Overlay hide={!open} disableAnimation/>
					<div tabIndex={-1}
					     data-cy={props['data-cy'] || Selectors.Modal}
					     role='dialog'
					     aria-modal='true'
					     aria-hidden={!open}
					     aria-labelledby={labelID}
					     aria-describedby={contentID}
					     className={join(styles.modalWindow, className)}>
						{children}
					</div>
				</Layer>
			</ElementsNavigator>
		</Fade>
	</ModalContext.Provider>;
}

export const ModalImpl = composeHOC(withModalManager, withErrorHandling)(Modal);
export {ModalImpl as Modal};
